import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Modal, Dropdown, Row, Col, Spinner } from 'react-bootstrap';
import { ChevronDown, Filter, Calendar, MapPin, X, Globe, Check, Crop, Users, TrendingUp, Search, Download } from 'react-feather';
import FilterUserDrawer from './FilterUserDrawer';
import {
  getCountryAndLanguage,
  getProvincesByCountry,
  getDistrictsByProvince,
  getCropShort,
  getFilteredFarmers,
  downloadFarmersData
} from '../lib/api';
import { DecryptFE, EncryptFE } from '../lib/encrypt';
import { Icon } from '@iconify/react/dist/iconify.js';

const FilterUser = () => {
  const [showDistrictModal, setShowDistrictModal] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState('Last Month');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedGender, setSelectedGender] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [ageRange, setAgeRange] = useState([18, 65]);
  const [selectedCrops, setSelectedCrops] = useState([]);
  const [activeTab, setActiveTab] = useState('state');
  const [loading, setLoading] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [cropsData, setCropsData] = useState([]);
  const [farmersData, setFarmersData] = useState([]);
  const [selectedProvinces, setSelectedProvinces] = useState([]);
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [filteredDistricts, setFilteredDistricts] = useState([]);
  const [filteredProvinces, setFilteredProvinces] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [countries, setCountries] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [geoLoading, setGeoLoading] = useState(false);

  const token = localStorage.getItem('token') || '';

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.country-dropdown')) {
        setCountryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Load provinces whenever selectedCountries changes
  useEffect(() => {
    const loadProvinces = async () => {
      if (selectedCountries.length === 0) {
        setProvinces([]);
        setDistricts([]);
        return;
      }

      try {
        setGeoLoading(true);
        const responses = await Promise.all(
          selectedCountries.map(cid => getProvincesByCountry(cid, token))
        );
        const merged = responses.flatMap(r => r?.data || []);
        setProvinces(merged);
        setSelectedProvinces(prev => prev.filter(p => selectedCountries.includes(p.CountryID)));
      } catch (err) {
        console.error('Error loading provinces:', err);
      } finally {
        setGeoLoading(false);
      }
    };

    loadProvinces();
  }, [selectedCountries]);

  // Load districts whenever selectedProvinces changes
  useEffect(() => {
    const loadDistricts = async () => {
      if (selectedProvinces.length === 0) {
        setDistricts([]);
        return;
      }

      try {
        setGeoLoading(true);
        const responses = await Promise.all(
          selectedProvinces.map(p => getDistrictsByProvince(p.ID || p.ProvinceID, token))
        );
        const merged = responses.flatMap(r => r?.data || []);
        setDistricts(merged);
        setSelectedDistricts(prev =>
          prev.filter(d => selectedProvinces.some(p => (p.ID || p.ProvinceID) === d.ProvinceID))
        );
      } catch (err) {
        console.error('Error loading districts:', err);
      } finally {
        setGeoLoading(false);
      }
    };

    loadDistricts();
  }, [selectedProvinces]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const geoRes = await getCountryAndLanguage(token);
      if (geoRes?.success) {
        setCountries(geoRes.data?.Countries || []);
        setLanguages(geoRes.data?.Languages || []);
      }

      const cropsResponse = await getCropShort(token);
      setCropsData(cropsResponse?.data || []);

      await fetchFilteredFarmers(false, false);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredFarmers = async (isFilterApplied = true, IsRegistrationDateFilterApplied = true) => {
    try {
      setLoading(true);

      // =================================================
      // DETECT WHICH FILTERS ARE ACTUALLY APPLIED BY USER
      // =================================================
      const hasCountries = selectedCountries.length > 0;
      const hasProvinces = selectedProvinces.length > 0;
      const hasDistricts = selectedDistricts.length > 0;
      const hasCrops = selectedCrops.length > 0;
      const hasGender = selectedGender.length > 0;

      // Age: only send if user moved sliders from default [18, 65]
      const isAgeChanged = ageRange[0] !== 18 || ageRange[1] !== 65;

      // Date: only send if user picked Custom Date with both dates
      const isCustomDate =
        selectedDateRange === 'Select Custom Date' && startDate && endDate;

      // Date range preset: only apply if user chose something other than default
      const isPresetDateRange =
        selectedDateRange !== 'Last Month' &&
        selectedDateRange !== 'From Beginning Of Time' &&
        selectedDateRange !== 'Select Custom Date';

      // =================================================
      // DETERMINE IF ANY FILTER IS APPLIED
      // =================================================
      const anyFilterApplied =
        hasCountries ||
        hasProvinces ||
        hasDistricts ||
        hasCrops ||
        hasGender ||
        isAgeChanged ||
        isCustomDate ||
        isPresetDateRange;

      // =================================================
      // BUILD FILTER PARAMS
      // =================================================
      const filterParams = {
        IsFilterApplied: anyFilterApplied ? 'true' : 'false',
        IsRegistrationDateFilterApplied: (isCustomDate || isPresetDateRange) ? 'true' : 'false',
        ResponseType: 'JSON',
      };

      // Only include params if user actually selected them
      if (hasCountries) {
        filterParams.CountryIDs = selectedCountries.join(',');
      }
      if (hasProvinces) {
        filterParams.ProvinceIDs = selectedProvinces
          .map(p => p.ProvinceID || p.ID || p.id)
          .filter(Boolean)
          .join(',');
      }
      if (hasDistricts) {
        filterParams.DistrictIDs = selectedDistricts
          .map(d => d.DistrictID || d.ID || d.id)
          .filter(Boolean)
          .join(',');
      }
      if (hasCrops) {
        filterParams.CropIDs = selectedCrops.join(',');
      }
      if (hasGender) {
        filterParams.Gender = selectedGender.join(',');
      }

      // Age: only send if changed
      if (isAgeChanged) {
        filterParams.FromAge = ageRange[0];
        filterParams.ToAge = ageRange[1];
      }

      // Date: only send if a real date filter is applied
      if (isCustomDate) {
        filterParams.FromRegistrationDate = startDate;
        filterParams.ToRegistrationDate = endDate;
      } else if (isPresetDateRange) {
        const dateRangeMap = {
          'Last 3 Month': 90,
          'Last 6 Month': 180,
        };
        if (dateRangeMap[selectedDateRange]) {
          const toDate = new Date().toISOString().split('T')[0];
          const fromDate = new Date();
          fromDate.setDate(fromDate.getDate() - dateRangeMap[selectedDateRange]);
          filterParams.FromRegistrationDate = fromDate.toISOString().split('T')[0];
          filterParams.ToRegistrationDate = toDate;
        }
      }

      const response = await getFilteredFarmers(filterParams, token);

      if (response?.status === 'success') {
        const farmersPersonal = response.data?.FarmersPersonalDetails || [];
        const farmersLocation = response.data?.FarmersLocationDetails || [];
        const farmersFarmingInterests = response.FarmersFarmingInterests || [];
        const farmersCropPhases = response.FarmersCropPhasePeriodDateDetail || [];
        const farmersFamilyInfo = response.FarmersFamilyInfo || [];

        const combinedData = farmersPersonal.map(personal => {
          const location = farmersLocation.find(loc => loc.FarmerID === personal.ID) || {};
          const farmingInterests = farmersFarmingInterests.filter(farm => farm.FarmerID === personal.ID);
          const cropPhases = farmersCropPhases.filter(phase => phase.FarmerID === personal.ID);
          const familyInfo = farmersFamilyInfo.filter(family => family.FarmerID === personal.ID);

          return {
            ...personal,
            ...location,
            FarmersFarmingInterests: farmingInterests,
            FarmersCropPhasePeriodDateDetail: cropPhases,
            FarmersFamilyInfo: familyInfo
          };
        });

        setFarmersData(combinedData);
      }
    } catch (error) {
      console.error('Error fetching filtered farmers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCountryChange = (countryId) => {
    const isSelected = selectedCountries.includes(countryId);
    const newSelectedCountries = isSelected
      ? selectedCountries.filter(c => c !== countryId)
      : [...selectedCountries, countryId];

    setSelectedCountries(newSelectedCountries);

    if (newSelectedCountries.length === 0) {
      setSelectedProvinces([]);
      setSelectedDistricts([]);
    }
  };

  const handleCropChange = (cropId) => {
    setSelectedCrops(prev =>
      prev.includes(cropId) ? prev.filter(c => c !== cropId) : [...prev, cropId]
    );
  };

  const handleGenderChange = (gender) => {
    setSelectedGender(prev => (prev.includes(gender) ? [] : [gender]));
  };

  const handleProvinceChange = (province) => {
    const pId = province.ID || province.ProvinceID;
    setSelectedProvinces(prev => {
      const exists = prev.some(p => (p.ID || p.ProvinceID) === pId);
      return exists ? prev.filter(p => (p.ID || p.ProvinceID) !== pId) : [...prev, province];
    });
  };

  const handleDistrictChange = (district) => {
    const dId = district.ID || district.DistrictID;
    setSelectedDistricts(prev => {
      const exists = prev.some(d => (d.ID || d.DistrictID) === dId);
      return exists ? prev.filter(d => (d.ID || d.DistrictID) !== dId) : [...prev, district];
    });
  };

  const toggleCategoryExpansion = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const clearAllFilters = () => {
    setSelectedCountries([]);
    setSelectedCrops([]);
    setSelectedGender([]);
    setSelectedDateRange('Last Month');
    setAgeRange([18, 65]);
    setSelectedProvinces([]);
    setSelectedDistricts([]);
    setStartDate('');
    setEndDate('');
  };

  const clearModalSelections = () => {
    if (activeTab === 'state') setSelectedProvinces([]);
    else setSelectedDistricts([]);
  };

  const handleSelectAll = (type) => {
    if (type === 'state') {
      if (selectedProvinces.length === provinces.length) {
        setSelectedProvinces([]);
      } else {
        setSelectedProvinces([...provinces]);
      }
    } else {
      if (selectedDistricts.length === districts.length) {
        setSelectedDistricts([]);
      } else {
        setSelectedDistricts([...districts]);
      }
    }
  };

  const applyFilters = async () => {
    setCurrentPage(1);
    await fetchFilteredFarmers();
  };

  const handleDownload = async (format) => {
    try {
      setDownloadLoading(true);

      const filterParams = {
        IsFilterApplied: true,
        IsRegistrationDateFilterApplied: false,
        CountryIDs: selectedCountries.join(','),
        ProvinceIDs: selectedProvinces.map(p => p.ID || p.ProvinceID).filter(Boolean).join(','),
        DistrictIDs: selectedDistricts.map(d => d.ID || d.DistrictID).filter(Boolean).join(','),
        CropIDs: selectedCrops.join(','),
        Gender: selectedGender.join(','),
        FromAge: ageRange[0],
        ToAge: ageRange[1],
        ResponseType: format.toUpperCase()
      };

      if (selectedDateRange === 'Select Custom Date' && startDate && endDate) {
        filterParams.FromRegistrationDate = startDate;
        filterParams.ToRegistrationDate = endDate;
      }

      const response = await downloadFarmersData(filterParams, format, token);
      const base64String = format === 'pdf' ? response.base64PDF : response.base64Excel;

      if (base64String) {
        const contentType = format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

        const byteCharacters = atob(base64String);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
          const slice = byteCharacters.slice(offset, offset + 512);
          const byteNumbers = new Array(slice.length);
          for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
          }
          byteArrays.push(new Uint8Array(byteNumbers));
        }

        const blob = new Blob(byteArrays, { type: contentType });
        const fileName = `farmers_${new Date().toISOString().split('T')[0]}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        throw new Error("No file data received from server");
      }
    } catch (error) {
      console.error(`Error downloading ${format}:`, error);
      alert(`Error downloading ${format}: ${error.message}`);
    } finally {
      setDownloadLoading(false);
    }
  };

  const groupedCrops = cropsData.reduce((acc, crop) => {
    if (!acc[crop.Category]) acc[crop.Category] = [];
    acc[crop.Category].push(crop);
    return acc;
  }, {});

  const handlePageChange = (page) => {
    if (page !== '...') setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  const genderMapping = {
    'Male': 'vtXtEBWaS2NWCijTDW13dw==',
    'Female': 'joyXnpjzEc0yRhHUVRQ4Cw==',
    'Other': '3V76nhuJOR55tAtnpFeJ2A=='
  };

  const filteredData = farmersData.filter(user => {
    if (!searchTerm) return true;
    const fullName = `${DecryptFE(user.GivenName || '')} ${DecryptFE(user.FamilyName || '')}`.toLowerCase();
    const country = (user.Country || '').toLowerCase();
    const state = (user.ProvinceOrState || '').toLowerCase();
    const district = (user.District || '').toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    return fullName.includes(searchLower) || country.includes(searchLower) || state.includes(searchLower) || district.includes(searchLower);
  });

  const totalFilteredUsers = filteredData.length;
  const totalPages = Math.ceil(totalFilteredUsers / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const slicedData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  const generatePageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= Math.min(4, totalPages); i++) {
      pages.push(i);
    }
    if (totalPages > 5) {
      pages.push('...');
      pages.push(totalPages);
    } else if (totalPages > 4) {
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="container-fluid py-3">
      {/* Filters Section Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4" style={{ paddingBottom: '30px' }}>
        <div className="d-flex align-items-center">
          <div className="mb-0 d-flex align-items-center" style={{ color: '#1B5E20', fontSize: '30px' }}>
            <Filter size={20} className="me-2" style={{ color: '#1B5E20' }} />
            Filter User
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Button
            variant="link"
            onClick={clearAllFilters}
            style={{ color: '#000', textDecoration: 'none', fontSize: '14px', fontWeight: '500', padding: '5px 15px', borderRadius: '4px', border: '1px solid #ccc', height: '40px' }}
          >
            Clear All
          </Button>

          <Dropdown>
            <Dropdown.Toggle
              variant="outline-success"
              style={{
                borderColor: '#1B5E20',
                fontSize: '14px',
                fontWeight: '500',
                padding: '5px 15px',
                height: '40px',
                display: 'flex',
                alignItems: 'center'
              }}
              disabled={downloadLoading}
            >
              {downloadLoading ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <Download size={16} className="me-2" />
              )}
              Download
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => handleDownload('pdf')} disabled={downloadLoading}>
                Download as PDF
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleDownload('excel')} disabled={downloadLoading}>
                Download as Excel
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <Button
            onClick={applyFilters}
            style={{
              backgroundColor: '#1B5E20',
              borderColor: '#1B5E20',
              fontWeight: '600',
              fontSize: '14px',
              padding: '5px 20px',
              height: '40px'
            }}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Loading...
              </>
            ) : 'Apply Filter'}
          </Button>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="p-3">
          <Row className="g-3 d-flex align-items-start">
            {/* Country Selection */}
            <Col xs={12} md={6} lg={4} xl={2} className="mb-3 mb-xl-0">
              <div className="mb-2 d-flex align-items-center">
                <Globe size={16} className="me-2" style={{ color: '#1B5E20' }} />
                <span className="fw-medium">Country</span>
              </div>

              <div className="position-relative country-dropdown">
                <div
                  className="border rounded p-2 d-flex align-items-center justify-content-between"
                  style={{
                    cursor: 'pointer',
                    backgroundColor: 'white',
                    minHeight: '40px'
                  }}
                  onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                >
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    {selectedCountries.length === 0
                      ? 'Select Countries'
                      : `${selectedCountries.length} selected`}
                  </span>
                  <ChevronDown
                    size={14}
                    style={{
                      transform: countryDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                      color: '#1B5E20'
                    }}
                  />
                </div>

                {countryDropdownOpen && (
                  <div className="border rounded mt-1 p-2 position-absolute w-100" style={{
                    backgroundColor: 'white',
                    zIndex: 1000,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                    {countries.map((country) => (
                      <div
                        key={country.ID}
                        className="px-2 py-2 d-flex align-items-center rounded"
                        onClick={() => handleCountryChange(country.ID)}
                        style={{
                          cursor: 'pointer',
                          backgroundColor: selectedCountries.includes(country.ID) ? '#E8F5E8' : 'transparent',
                          margin: '2px 0'
                        }}
                      >
                        {country.ImageOfFlag ? (
                          <img
                            src={`https://api.soksan-dss.com/uploads/flag/${country.ImageOfFlag}`}
                            alt={country.CountryName}
                            style={{ width: '20px', height: '15px', marginRight: '8px' }}
                          />
                        ) : (
                          <span role="img" aria-label={country.CountryName} className="me-2" style={{ fontSize: '18px' }}>🌐</span>
                        )}
                        <span className="flex-grow-1" style={{ fontSize: '14px' }}>{country.CountryName}</span>
                        {selectedCountries.includes(country.ID) && (
                          <Check size={16} className="ms-auto" color="#1B5E20" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button
                variant="success"
                className="w-100 mt-72"
                onClick={() => setShowDistrictModal(true)}
                style={{
                  backgroundColor: '#1B5E20',
                  borderColor: '#1B5E20',
                  fontSize: '14px',
                  fontWeight: '500',
                  padding: '4px 12px'
                }}
              >
                Filter By State & District
              </Button>
            </Col>

            {/* Date Filter */}
            <Col xs={12} md={6} lg={4} xl={4} className="mb-3 mb-xl-0">
              <div className="mb-2 d-flex align-items-center">
                <Calendar size={16} className="me-2" style={{ color: '#1B5E20' }} />
                <span className="fw-medium">Date</span>
              </div>

              <div className="d-flex flex-wrap gap-1 mb-3">
                {['Last Month', 'Last 3 Month', 'Last 6 Month', 'From Beginning Of Time', 'Select Custom Date'].map((range, index) => (
                  <Button
                    key={index}
                    onClick={() => setSelectedDateRange(range)}
                    className="py-1 px-3"
                    style={{
                      fontSize: '13px',
                      fontWeight: '500',
                      backgroundColor: selectedDateRange === range ? '#1B5E20' : 'transparent',
                      borderColor: selectedDateRange === range ? '#1B5E20' : '#ccc',
                      color: selectedDateRange === range ? 'white' : '#666',
                      borderRadius: '15px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {range}
                  </Button>
                ))}
              </div>

              <div className="mb-2">
                <div className="d-flex flex-column flex-md-row align-items-center gap-2">
                  <div className="w-100">
                    <Form.Label className="text-muted small mb-1">Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      size="sm"
                      style={{ fontSize: '14px' }}
                      className="w-100"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      disabled={selectedDateRange !== 'Select Custom Date'}
                    />
                  </div>
                  <span className="text-muted d-none d-md-block">-</span>
                  <div className="w-100">
                    <Form.Label className="text-muted small mb-1">End Date</Form.Label>
                    <Form.Control
                      type="date"
                      size="sm"
                      style={{ fontSize: '14px' }}
                      className="w-100"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      disabled={selectedDateRange !== 'Select Custom Date'}
                    />
                  </div>
                </div>
              </div>
            </Col>

            {/* Crop Selection */}
            <Col xs={12} md={6} lg={4} xl={2} className="mb-3 mb-xl-0">
              <div className="mb-2 d-flex align-items-center gap-2">
                <img src='assets/images/GroupCrop.svg' alt="crop" />
                <span>Crop</span>
              </div>

              <div className="border rounded p-2" style={{
                maxHeight: '280px',
                overflowY: 'auto',
                backgroundColor: 'white'
              }}>
                {Object.entries(groupedCrops).map(([category, crops]) => (
                  <div key={category} className="mb-3">
                    <div
                      className="d-flex align-items-center justify-content-between p-2 rounded mb-2"
                      style={{
                        backgroundColor: '#E8F5E8',
                        color: '#1B5E20',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                      onClick={() => toggleCategoryExpansion(category)}
                    >
                      <span>{category}</span>
                      <ChevronDown
                        size={14}
                        style={{
                          transform: expandedCategories[category] ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s'
                        }}
                      />
                    </div>

                    {expandedCategories[category] && (
                      <div className="ms-3">
                        {crops.map((crop) => (
                          <Form.Check
                            key={crop.ID}
                            type="checkbox"
                            id={`crop-check-${crop.ID}`}
                            label={crop.CropName}
                            checked={selectedCrops.includes(crop.ID)}
                            onChange={() => handleCropChange(crop.ID)}
                            className="mb-2 py-1"
                            style={{ fontSize: '14px' }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Col>

            {/* Gender Filter */}
            <Col xs={8} md={4} lg={3} xl={2} className="mb-3 mb-xl-0">
              <div className="mb-2 d-flex align-items-center">
                <Users size={16} className="me-2" style={{ color: '#1B5E20' }} />
                <span className="fw-medium">Gender</span>
              </div>

              <div className="d-flex flex-column gap-2">
                {['Male', 'Female', 'Other'].map((gender, index) => (
                  <Button
                    key={index}
                    onClick={() => handleGenderChange(genderMapping[gender])}
                    className="py-2"
                    style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      textAlign: 'start',
                      backgroundColor: selectedGender.includes(genderMapping[gender]) ? '#126B3A' : 'transparent',
                      borderColor: selectedGender.includes(genderMapping[gender]) ? '#126B3A' : '#ccc',
                      color: selectedGender.includes(genderMapping[gender]) ? 'white' : '#000',
                      borderRadius: '6px',
                      transition: 'all 0.2s'
                    }}
                  >
                    {gender}
                  </Button>
                ))}
              </div>
            </Col>

            {/* Age Range */}
            <Col xs={12} md={6} lg={4} xl={2} className="mb-3 mb-xl-0">
              <div className="mb-2 d-flex align-items-center">
                <TrendingUp size={16} className="me-2" style={{ color: '#1B5E20' }} />
                <span className="fw-medium">Age</span>
              </div>

              <div className="border rounded p-3">
                <div className="d-flex justify-content-between mb-2">
                  <span className="small fw-medium">Min Age: {ageRange[0]}</span>
                  <span className="small fw-medium">Max Age: {ageRange[1]}</span>
                </div>

                <div className="mb-3">
                  <label className="small fw-medium">Minimum Age</label>
                  <Form.Range
                    min={10}
                    max={100}
                    value={ageRange[0]}
                    onChange={(e) => {
                      const newMin = parseInt(e.target.value);
                      setAgeRange([newMin, Math.max(newMin, ageRange[1])]);
                    }}
                    className="w-100"
                    style={{ accentColor: '#1B5E20' }}
                  />
                </div>

                <div className="mb-3">
                  <label className="small fw-medium">Maximum Age</label>
                  <Form.Range
                    min={10}
                    max={100}
                    value={ageRange[1]}
                    onChange={(e) => {
                      const newMax = parseInt(e.target.value);
                      setAgeRange([Math.min(ageRange[0], newMax), newMax]);
                    }}
                    className="w-100"
                    style={{ accentColor: '#1B5E20' }}
                  />
                </div>

                <div className="d-flex justify-content-center mt-3">
                  <span className="fw-bold text-nowrap" style={{ color: '#1B5E20', fontSize: '16px' }}>
                    {ageRange[0]} - {ageRange[1]}
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <div className="d-flex flex-column flex-lg-row justify-content-between align-items-center mb-4 border-bottom pb-3 gap-3">
            <div className="d-flex flex-column flex-md-row align-items-center gap-3 w-100">
              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                <div className="mb-0" style={{ color: '#1B5E20', fontWeight: '500', fontSize: '20px', whiteSpace: 'nowrap' }}>
                  User Result
                </div>

                <div
                  className='rounded-4'
                  style={{
                    color: 'black',
                    padding: '2px 9px',
                    fontSize: '14px',
                    backgroundColor: '#E8F5E8',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {totalFilteredUsers} User{totalFilteredUsers !== 1 ? 's' : ''}
                </div>
              </div>

              <div className="input-group input-group-sm ms-md-4 w-100" style={{ maxWidth: '600px', height: '40px' }}>
                <Form.Control
                  type="text"
                  placeholder="Search users..."
                  className="border-end-0"
                  style={{ fontSize: '15px' }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="input-group-text bg-white" style={{ borderColor: '#ced4da' }}>
                  <Search size={16} color="#666" />
                </span>
              </div>
            </div>

            <div className="d-flex align-items-center flex-shrink-0">
              <div className="d-flex align-items-center" style={{ fontSize: '14px', color: '#000' }}>
                <span className="me-2 fw-medium">Show</span>
                <Form.Select
                  size="sm"
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(e.target.value)}
                  style={{
                    width: '70px',
                    fontSize: '14px',
                    height: '40px',
                    borderRadius: '8px'
                  }}
                  className="fw-medium"
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </Form.Select>
              </div>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="success" />
              <p className="mt-2">Loading users...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0" style={{ borderCollapse: 'separate', borderSpacing: '0 5px' }}>
                  <thead style={{ backgroundColor: '#51AE3B21', border: '1px solid #E8F5E9' }}>
                    <tr>
                      <th style={{ color: '#1B5E20', fontWeight: '600', borderTop: 'none', padding: '12px 10px' }}>User Name</th>
                      <th style={{ color: '#1B5E20', fontWeight: '600', borderTop: 'none', padding: '12px 10px' }}>Date Of Registration</th>
                      <th style={{ color: '#1B5E20', fontWeight: '600', borderTop: 'none', padding: '12px 10px' }}>Country</th>
                      <th style={{ color: '#1B5E20', fontWeight: '600', borderTop: 'none', padding: '12px 10px' }}>State</th>
                      <th style={{ color: '#1B5E20', fontWeight: '600', borderTop: 'none', padding: '12px 10px' }}>District</th>
                      <th style={{ color: '#1B5E20', fontWeight: '600', borderTop: 'none', padding: '12px 10px' }}>Commune</th>
                      <th style={{ color: '#1B5E20', fontWeight: '600', borderTop: 'none', padding: '12px 10px' }}>Village</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slicedData.map((user, index) => (
                      <tr
                        key={user.ID || index}
                        onClick={() => setSelectedUser(user)}
                        style={{
                          cursor: 'pointer',
                          backgroundColor: selectedUser?.ID === user.ID ? '#F1F8E9' : 'transparent',
                          borderBottom: '1px solid #eee'
                        }}
                      >
                        <td style={{ verticalAlign: 'middle', fontWeight: '500', color: '#333', padding: '10px' }}>
                          {user.GivenName || user.FamilyName ? `${DecryptFE(user.GivenName)} ${DecryptFE(user.FamilyName)}` : 'N/A'}
                        </td>
                        <td style={{ verticalAlign: 'middle', color: '#666', padding: '10px', fontSize: '14px' }}>
                          {new Date(user.CreatedOn).toLocaleDateString()}
                        </td>
                        <td style={{ verticalAlign: 'middle', fontSize: '14px', padding: '10px' }}>
                          {user.CountryImageName ? (
                            <img
                              src={`https://api.soksan-dss.com/uploads/flag/${user.CountryImageName}`}
                              alt={user.CountryImageName}
                              style={{ width: '20px', height: '15px', marginRight: '8px' }}
                            />
                          ) : (
                            <span role="img" aria-label={user.CountryImageName} className="me-2" style={{ fontSize: '18px' }}>
                              <Icon icon="streamline-ultimate-color:network-pin" />
                            </span>
                          )}
                        </td>
                        <td style={{ verticalAlign: 'middle', color: '#666', padding: '10px', fontSize: '14px' }}>
                          {user.ProvinceOrState || 'N/A'}
                        </td>
                        <td style={{ verticalAlign: 'middle', color: '#666', padding: '10px', fontSize: '14px' }}>
                          {user.District || 'N/A'}
                        </td>
                        <td style={{ verticalAlign: 'middle', color: '#666', padding: '10px', fontSize: '14px' }}>
                          {user.Commune || 'N/A'}
                        </td>
                        <td style={{ verticalAlign: 'middle', color: '#666', padding: '10px', fontSize: '14px' }}>
                          {user.Village || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 pt-3 border-top gap-3">
                <div className="text-muted text-center text-md-start" style={{ fontSize: '14px' }}>
                  Showing <strong>{slicedData.length}</strong> Result Out <strong>{totalFilteredUsers}</strong> Users
                </div>

                <div className="d-flex align-items-center justify-content-center">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="px-2 px-sm-3 me-1 me-sm-2"
                    style={{
                      borderColor: '#C8E6C9',
                      color: currentPage === 1 ? '#999' : '#666',
                      fontSize: '14px'
                    }}
                  >
                    &lt; <span className="d-none d-sm-inline">Previous</span>
                  </Button>

                  <div className="d-none d-sm-flex">
                    {generatePageNumbers().map((page, index) => (
                      <Button
                        key={index}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        disabled={page === '...'}
                        className="mx-1"
                        style={{
                          minWidth: '32px',
                          padding: '6px 12px',
                          backgroundColor: page === currentPage ? '#1B5E20' : 'transparent',
                          borderColor: page === currentPage ? '#1B5E20' : '#C8E6C9',
                          color: page === currentPage ? '#fff' : '#666',
                          fontWeight: page === currentPage ? 'bold' : 'normal',
                          fontSize: '14px'
                        }}
                      >
                        {page}
                      </Button>
                    ))}
                  </div>

                  <span className="d-sm-none mx-2 text-muted" style={{ fontSize: '14px' }}>
                    {currentPage} / {totalPages}
                  </span>

                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="px-2 px-sm-3 ms-1 ms-sm-2"
                    style={{
                      borderColor: '#C8E6C9',
                      color: currentPage === totalPages ? '#999' : '#666',
                      fontSize: '14px'
                    }}
                  >
                    <span className="d-none d-sm-inline">Next</span> &gt;
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Inlined State & District Modal (Prevents unmounting/flicker) */}
      <Modal show={showDistrictModal} onHide={() => setShowDistrictModal(false)} size="md">
        <Modal.Header closeButton style={{ borderBottom: '2px solid #C8E6C9', padding: '16px 24px' }}>
          <Modal.Title style={{ color: '#1B5E20', fontWeight: '500', fontSize: '18px' }}>
            Filter By State & District
            {selectedCountries.length > 0 && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Filtering for selected countries
              </div>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '24px' }}>
          <div className="mb-4">
            <div className="d-flex" style={{ borderBottom: '1px solid #e0e0e0' }}>
              <div
                className="px-3 py-2 cursor-pointer"
                onClick={() => setActiveTab('state')}
                style={{
                  fontSize: '14px',
                  color: activeTab === 'state' ? '#1B5E20' : '#666',
                  fontWeight: activeTab === 'state' ? '600' : 'normal',
                  backgroundColor: activeTab === 'state' ? '#F1F8E9' : 'transparent',
                  borderRadius: '4px 4px 0 0',
                  borderBottom: activeTab === 'state' ? '4px solid #1B5E20' : '4px solid transparent',
                  marginRight: '10px',
                  cursor: 'pointer',
                  lineHeight: '1.2'
                }}
              >
                State
              </div>
              <div
                className="px-3 py-2 cursor-pointer"
                onClick={() => setActiveTab('district')}
                style={{
                  fontSize: '14px',
                  color: activeTab === 'district' ? '#1B5E20' : '#666',
                  fontWeight: activeTab === 'district' ? '600' : 'normal',
                  backgroundColor: activeTab === 'district' ? '#F1F8E9' : 'transparent',
                  borderRadius: '4px 4px 0 0',
                  borderBottom: activeTab === 'district' ? '4px solid #1B5E20' : '4px solid transparent',
                  marginRight: '10px',
                  cursor: 'pointer',
                  lineHeight: '1.2'
                }}
              >
                District
              </div>
            </div>
          </div>

          {geoLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" variant="success" />
              <div className="text-muted small mt-2">Updating regions...</div>
            </div>
          ) : activeTab === 'state' ? (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Button
                  variant="link"
                  size="sm"
                  style={{ color: '#1B5E20', textDecoration: 'none', fontSize: '13px', fontWeight: '500', padding: '0' }}
                  onClick={() => handleSelectAll('state')}
                >
                  {selectedProvinces.length === provinces.length && provinces.length > 0 ? 'Unselect All' : 'Select All'}
                </Button>
              </div>
              {provinces.length === 0 ? (
                <div className="text-center py-4" style={{ color: '#666' }}>
                  {selectedCountries.length > 0 ? 'No states available for selected countries' : 'Please select a country first'}
                </div>
              ) : (
                <div style={{ maxHeight: '300px', overflowY: 'auto', paddingRight: '8px' }}>
                  {provinces.map((province) => {
                    const provId = province.ID || province.ProvinceID;
                    const isSelected = selectedProvinces.some(p => (p.ID || p.ProvinceID) === provId);

                    return (
                      <div
                        key={provId}
                        className="d-flex align-items-center py-2 px-2 mb-1"
                        style={{
                          backgroundColor: isSelected ? '#F1F8E9' : 'transparent',
                          borderRadius: '4px',
                          minHeight: '36px',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleProvinceChange(province)}
                      >
                        <div className="me-3" style={{ minWidth: '20px' }}>
                          <Form.Check
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            style={{ margin: 0, pointerEvents: 'none' }}
                          />
                        </div>
                        <label
                          style={{
                            color: '#1B5E20',
                            fontSize: '13px',
                            cursor: 'pointer',
                            margin: 0,
                            flex: 1,
                            fontWeight: isSelected ? '500' : '400',
                            pointerEvents: 'none'
                          }}
                        >
                          {province.ProvinceName}
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Button
                  variant="link"
                  size="sm"
                  style={{ color: '#1B5E20', textDecoration: 'none', fontSize: '13px', fontWeight: '500', padding: '0' }}
                  onClick={() => handleSelectAll('district')}
                >
                  {selectedDistricts.length === districts.length && districts.length > 0 ? 'Unselect All' : 'Select All'}
                </Button>
              </div>
              {districts.length === 0 ? (
                <div className="text-center py-4" style={{ color: '#666' }}>
                  {selectedProvinces.length > 0 ? 'No districts available for selected states' : 'Please select a state first'}
                </div>
              ) : (
                <div style={{ maxHeight: '300px', overflowY: 'auto', paddingRight: '8px' }}>
                  {districts.map((district) => {
                    const distId = district.ID || district.DistrictID;
                    const isSelected = selectedDistricts.some(d => (d.ID || d.DistrictID) === distId);

                    return (
                      <div
                        key={distId}
                        className="d-flex align-items-center py-2 px-2 mb-1"
                        style={{
                          backgroundColor: isSelected ? '#F1F8E9' : 'transparent',
                          borderRadius: '4px',
                          minHeight: '36px',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleDistrictChange(district)}
                      >
                        <div className="me-3" style={{ minWidth: '20px' }}>
                          <Form.Check
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            style={{ margin: 0, pointerEvents: 'none' }}
                          />
                        </div>
                        <label
                          style={{
                            color: '#1B5E20',
                            fontSize: '13px',
                            cursor: 'pointer',
                            margin: 0,
                            flex: 1,
                            fontWeight: isSelected ? '500' : '400',
                            pointerEvents: 'none'
                          }}
                        >
                          {district.DistrictName}
                          <div style={{ fontSize: '11px', color: '#666' }}>
                            {district.ProvinceName}
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ padding: '16px 24px', borderTop: '1px solid #e0e0e0' }}>
          <div className="d-flex justify-content-between w-100 align-items-center">
            <Button
              variant="outline-secondary"
              onClick={clearModalSelections}
              style={{
                borderColor: '#C8E6C9',
                color: '#666',
                fontSize: '14px',
                padding: '6px 16px',
                fontWeight: '500'
              }}
            >
              Clear All
            </Button>
            <div>
              <Button
                variant="outline-secondary"
                onClick={() => setShowDistrictModal(false)}
                style={{
                  borderColor: '#C8E6C9',
                  color: '#666',
                  marginRight: '12px',
                  fontSize: '14px',
                  padding: '6px 16px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </Button>
              <Button
                style={{
                  backgroundColor: '#1B5E20',
                  borderColor: '#1B5E20',
                  fontSize: '14px',
                  padding: '6px 20px',
                  fontWeight: '500'
                }}
                onClick={() => {
                  setShowDistrictModal(false);
                  applyFilters();
                }}
              >
                Apply Filter
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {/* User Detail Drawer */}
      {selectedUser && (
        <FilterUserDrawer
          selectedUser={selectedUser}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </div>
  );
};

export default FilterUser;